import React from 'react';
import { XCircle, X as XMark } from 'lucide-react';
export function ProblemSection() {
  return <section className="py-20 px-6 bg-white border-y border-[#D4C5A9]/20" data-id="element-228">
      <div className="max-w-[720px] mx-auto" data-id="element-229">
        {/* The Real Problem */}
        <div className="mb-16 fade-in-up" data-id="element-230">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2B4257] mb-8" data-id="element-231">
            You don't have a "stuff problem." <br data-id="element-232" />
            <span className="italic text-[#6B9B8A]" data-id="element-233">
              You have a decision problem.
            </span>
          </h2>

          <p className="text-lg text-[#5A5A5A] mb-6 leading-relaxed" data-id="element-234">
            Because when you pick something up, you're not just deciding about
            an object. You're deciding about:
          </p>

          <ul className="space-y-4 mb-8 pl-6 border-l-2 border-[#D4C5A9]" data-id="element-235">
            <li className="text-lg text-[#1A1A1A]" data-id="element-236">
              The man you were when you bought it
            </li>
            <li className="text-lg text-[#1A1A1A]" data-id="element-237">The chapter it came from</li>
            <li className="text-lg text-[#1A1A1A]" data-id="element-238">
              Whether keeping it proves something
            </li>
            <li className="text-lg text-[#1A1A1A]" data-id="element-239">
              Whether letting it go means you're losing something
            </li>
          </ul>

          <p className="text-lg text-[#5A5A5A] mb-4 leading-relaxed" data-id="element-240">
            So you stall.
          </p>
          <p className="text-lg text-[#5A5A5A] mb-4 leading-relaxed" data-id="element-241">
            You create "maybe piles."
          </p>
          <p className="text-lg text-[#5A5A5A] mb-4 leading-relaxed" data-id="element-242">
            You promise you'll get to it next weekend.
          </p>
          <p className="text-lg text-[#5A5A5A] mb-8 leading-relaxed font-medium text-[#1A1A1A]" data-id="element-243">
            And next weekend becomes next month.
          </p>

          <div className="bg-[#FAFAF7] p-8 rounded-lg border border-[#D4C5A9]/30 mb-8" data-id="element-244">
            <p className="font-bold text-[#2B4257] mb-4 uppercase tracking-wide text-base" data-id="element-245">
              Meanwhile:
            </p>
            <ul className="space-y-3" data-id="element-246">
              <li className="flex items-start text-base text-[#5A5A5A]" data-id="element-247">
                <span className="mr-3 text-[#D4C5A9]" data-id="element-248">•</span> Your spouse gets
                tense
              </li>
              <li className="flex items-start text-base text-[#5A5A5A]" data-id="element-249">
                <span className="mr-3 text-[#D4C5A9]" data-id="element-250">•</span> Your kids start
                "helping" (which feels like judging)
              </li>
              <li className="flex items-start text-base text-[#5A5A5A]" data-id="element-251">
                <span className="mr-3 text-[#D4C5A9]" data-id="element-252">•</span> The house becomes
                a pressure cooker
              </li>
              <li className="flex items-start text-base text-[#5A5A5A]" data-id="element-253">
                <span className="mr-3 text-[#D4C5A9]" data-id="element-254">•</span> And the deadline
                doesn't care how you feel
              </li>
            </ul>
          </div>

          <p className="text-xl font-serif italic text-[#2B4257] border-l-4 border-[#2B4257] pl-6 py-2" data-id="element-255">
            If any of that hits a little too close… This is your way out.
          </p>
        </div>

        {/* Divider */}
        <hr className="border-[#D4C5A9]/30 mb-16" data-id="element-256" />

        {/* Why Old Solutions Fail */}
        <div className="fade-in-up delay-100" data-id="element-257">
          <h3 className="text-2xl md:text-3xl font-bold text-[#2B4257] mb-6" data-id="element-258">
            The downsizing advice you see online is built to fail you.
          </h3>

          <p className="text-lg text-[#5A5A5A] mb-8 leading-relaxed" data-id="element-259">
            Because it's written for people who think downsizing is a cleaning
            project. So they give you:
          </p>

          <ul className="space-y-3 mb-10 max-w-md" data-id="element-260">
            {['Generic checklists', '"Declutter hacks"', 'Motivational quotes', '"Just be ruthless" energy'].map(item => <li key={item} className="flex items-center text-base text-[#5A5A5A]" data-id="element-261">
                <XMark className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" data-id="element-262" />
                <span className="line-through decoration-red-300" data-id="element-263">{item}</span>
              </li>)}
          </ul>

          <div className="bg-red-50 p-6 rounded-lg border border-red-100 mb-8" data-id="element-264">
            <h4 className="font-bold text-red-800 mb-3 flex items-center text-base" data-id="element-265">
              <XCircle className="w-5 h-5 mr-2" data-id="element-266" />
              None of it works when the real problem is:
            </h4>
            <ul className="space-y-2 text-red-900/80 text-base" data-id="element-267">
              <li data-id="element-268">• Emotional decision fatigue</li>
              <li data-id="element-269">• Grief you didn't ask for</li>
              <li data-id="element-270">
                • And family dynamics that turn your home into a courtroom
              </li>
            </ul>
          </div>

          {/* Pull quote */}
          <div className="bg-[#D4C5A9]/15 p-8 rounded-lg border-l-4 border-[#2B4257] mb-10" data-id="element-271">
            <p className="text-xl text-[#2B4257] font-bold mb-4" data-id="element-272">
              You don't need harsher advice.
            </p>
            <p className="text-lg text-[#2B4257] leading-relaxed" data-id="element-273">
              You need a system that respects two things at the same time:
            </p>
            <div className="mt-4 space-y-2" data-id="element-274">
              <p className="text-lg font-semibold text-[#2B4257]" data-id="element-275">
                1) Your life mattered.
              </p>
              <p className="text-lg font-semibold text-[#2B4257]" data-id="element-276">
                2) You still need to finish.
              </p>
            </div>
          </div>

          {/* Mid-page CTA */}
          <div className="text-center" data-id="element-277">
            <a href="#pricing" className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all duration-200 bg-[#2B4257] rounded-md hover:bg-[#1f3040] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2B4257]" data-id="element-278">
              Get the Playbook — $6.36{' '}
              <span className="ml-2 line-through opacity-60 text-sm" data-id="element-279">$39</span>
            </a>
          </div>
        </div>
      </div>
    </section>;
}